import React, { useState, useEffect, useCallback } from 'react';
import { REACT_APP_DISCORD_BOT_ENDPOINT } from '../config/constants';
import '../styles/DomainUpdateForm.css';

const initialState = {
  id: '',
  url: '',
  ip: '',
  name: '',
  plan: '',
  tipo_plano: '',
  email: '',
  type: '',
  idvenda: '',
  mute: '',
  cooldown: '',
  status: '',
  update: true,
  subdomain: '',
  ssh_definitions: '',
  provider: '',
};

function isValidUrl(url) {
  try {
    const pattern = /^(https?:\/\/)?([\w-]+\.)+[\w-]{2,}(\/.*)?$/i;
    return pattern.test(url);
  } catch {
    return false;
  }
}

function isValidIPv4(ip) {
  if (!ip) return true; // Campo opcional, só valida se preenchido
  const pattern = /^(25[0-5]|2[0-4]\d|1\d{2}|[1-9]?\d)(\.(25[0-5]|2[0-4]\d|1\d{2}|[1-9]?\d)){3}$/;
  return pattern.test(ip);
}

function sanitizeUrlToId(url) {
  if (!url) return '';
  let sanitized = url.replace(/^https?:\/\//, '').replace(/\/$/, '');
  return sanitized;
}

// Busca os tipos de plano do .env
let tipoPlanos = {};
let planosEnv;
try {
  if (process.env.REACT_APP_TIPO_DE_PLANOS) {
    planosEnv = JSON.parse(process.env.REACT_APP_TIPO_DE_PLANOS);
    for (let tpopn in planosEnv) {
      Object.assign(tipoPlanos, planosEnv[tpopn]);
    }
    planosEnv = Object.keys(planosEnv).sort((a, b) => a.localeCompare(b, 'pt-BR'));
  }
} catch {
  tipoPlanos = {};
}
const tipoPlanosKeys = Object.keys(tipoPlanos).sort((a, b) => a.localeCompare(b, 'pt-BR'));

// Busca os providers do .env
let providersEnv = [];
try {
  if (process.env.REACT_APP_PROVIDER) {
    providersEnv = JSON.parse(process.env.REACT_APP_PROVIDER);
  }
} catch {
  providersEnv = [];
}

export default function DomainForm({ authToken }) {
  const [form, setForm] = useState(initialState);
  const [submitted, setSubmitted] = useState(false);
  const [urlTouched, setUrlTouched] = useState(false);
  const [ipTouched, setIpTouched] = useState(false);
  const [responseMsg, setResponseMsg] = useState('');
  
  // Estados para busca de domínio
  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const [isMouseOverDropdown, setIsMouseOverDropdown] = useState(false);
  const [isUpdateMode, setIsUpdateMode] = useState(false);

  // Função para obter tipos de plano baseado no plan selecionado
  const getAvailableTipoPlanos = (selectedPlan) => {
    if (!selectedPlan || !process.env.REACT_APP_TIPO_DE_PLANOS) {
      return tipoPlanosKeys;
    }
    
    try {
      const planosData = JSON.parse(process.env.REACT_APP_TIPO_DE_PLANOS);
      const planData = planosData[selectedPlan];
      
      if (planData) {
        return Object.keys(planData).sort((a, b) => a.localeCompare(b, 'pt-BR'));
      }
    } catch (error) {
      console.error('Erro ao processar tipos de plano:', error);
    }
    
    return tipoPlanosKeys;
  };

  // Obter tipos de plano disponíveis para o plan atual
  const availableTipoPlanos = getAvailableTipoPlanos(form.plan);

  const searchDomains = useCallback(async () => {
    if (!searchTerm || searchTerm.length < 3) return;
    setIsSearching(true);
    try {
      const requestBody = {
        command: 'opt',
        function: 'search-domains',
        data: {
          search: searchTerm
        }
      };
      
      const response = await fetch(REACT_APP_DISCORD_BOT_ENDPOINT, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authToken}`
        },
        body: JSON.stringify(requestBody),
      });
      
      const result = await response.json();
      
      // Verifica se é um array direto ou objeto com success/data
      let searchData = null;
      if (Array.isArray(result)) {
        searchData = result;
      } else if (result.success && result.data) {
        searchData = result.data;
      }
      
      if (searchData && searchData.length > 0) {
        setSearchResults(searchData);
        setShowDropdown(true);
      } else {
        setSearchResults([]);
        setShowDropdown(false);
      }
    } catch (error) {
      console.error('Erro na busca:', error);
      setSearchResults([]);
      setShowDropdown(false);
    } finally {
      setIsSearching(false);
    }
  }, [searchTerm, authToken]);

  // Debounce para busca
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (searchTerm.length >= 3) {
        searchDomains();
      } else {
        setSearchResults([]);
        // Não fecha o dropdown automaticamente, só quando não há resultados
        if (searchResults.length === 0) {
          setShowDropdown(false);
        }
      }
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [searchTerm, searchResults.length, searchDomains]);

  const handleDomainSelect = (domain) => {
    setIsUpdateMode(true);
    setForm({
      ...initialState,
      id: domain.id || '',
      url: domain.url || '',
      ip: domain.ip || '',
      name: domain.name || '',
      plan: domain.plan || '',
      tipo_plano: domain.type || '',
      email: domain.email || '',
      type: domain.type || '',
      idvenda: domain.idvenda || '',
      mute: domain.mute || '',
      cooldown: domain.cooldown || '',
      status: domain.status || '',
      update: domain.update !== undefined ? domain.update : true,
      subdomain: domain.subdomain || '',
      ssh_definitions: domain.ssh_definitions || '',
      provider: domain.provider || '',
    });
    setSearchTerm(domain.url || domain.name || '');
    setShowDropdown(false);
    setIsMouseOverDropdown(false);
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    if (name === 'url') {
      setForm((prev) => ({
        ...prev,
        url: value,
        id: sanitizeUrlToId(value),
      }));
    } else if (name === 'plan') {
      // Quando o plan muda, limpa o tipo_plano
      setForm((prev) => ({
        ...prev,
        plan: value,
        tipo_plano: '', // Limpa o tipo_plano quando o plan muda
      }));
    } else {
      setForm((prev) => ({
        ...prev,
        [name]: type === 'checkbox' ? checked : value,
      }));
    }
  };

  const handleUrlBlur = () => {
    setUrlTouched(true);
  };

  const handleIpBlur = () => {
    setIpTouched(true);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    const command = 'submit';
    const formType = isUpdateMode ? 'domain_update' : 'domain_register';
    
    fetch(REACT_APP_DISCORD_BOT_ENDPOINT, {
      method: 'POST',
      headers: { 
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${authToken}`
      },
      body: JSON.stringify({
        command: command,
        data: {
          form: formType,
          idvenda: form.idvenda,
          url: form.url,
          ip: form.ip,
          name: form.name,
          provider: form.provider,
          plan: form.plan,
          tipo_plano: form.tipo_plano,
          email: form.email,
          update: form.update,
        }
      }),
    })
      .then((res) => res.text())
      .then((msg) => {
        setSubmitted(true);
        setResponseMsg(msg);
      });
  };

  const handleClearForm = () => {
    setForm(initialState);
    setSearchTerm('');
    setIsUpdateMode(false);
    setSubmitted(false);
    setResponseMsg('');
    setUrlTouched(false);
    setIpTouched(false);
  };

  const handleDeleteDomain = async () => {
    if (!form.id || !window.confirm('Tem certeza que deseja apagar este domínio? Esta ação não pode ser desfeita.')) {
      return;
    }

    try {
      const response = await fetch(REACT_APP_DISCORD_BOT_ENDPOINT, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authToken}`
        },
        body: JSON.stringify({
          command: 'submit',
          data: {
            form: 'domain_delete',
            id: form.id
          }
        })
      });

      const result = await response.json();

      if (result.message && result.message.toLowerCase().includes('sucesso')) {
        alert('Domínio apagado com sucesso!');
        // Limpeza completa do formulário como se estivesse começando do zero
        setForm(initialState);
        setSearchTerm('');
        setSearchResults([]);
        setIsUpdateMode(false);
        setSubmitted(false);
        setResponseMsg('');
        setUrlTouched(false);
        setIpTouched(false);
        setShowDropdown(false);
        setIsMouseOverDropdown(false);
        setIsSearching(false);
      } else {
        alert(`Erro ao apagar domínio: ${result.message || 'Erro desconhecido'}`);
      }
    } catch (error) {
      console.error('Erro ao apagar domínio:', error);
      alert('Erro ao apagar domínio. Verifique a conexão e tente novamente.');
    }
  };

  const urlIsValid = isValidUrl(form.url);
  const urlShowError = urlTouched && !urlIsValid && form.url.length > 0;
  const ipIsValid = isValidIPv4(form.ip);
  const ipShowError = ipTouched && !ipIsValid && form.ip.length > 0;
  const submitDisabled = (!urlIsValid && form.url.length > 0) || (form.ip.length > 0 && !ipIsValid) || !form.provider || !form.plan || !form.tipo_plano || !form.email;

  if (submitted) {
    return (
      <div className="dynamic-form">
        <div className={`success-message${responseMsg && responseMsg.toLowerCase().includes('falha') ? ' error-message' : ''}`}>
          {responseMsg ? responseMsg : `${isUpdateMode ? 'Domínio atualizado' : 'Domínio cadastrado'} com sucesso!`}
        </div>
        <div className="form-actions">
          <button onClick={handleClearForm} className="submit-button">
            Novo Domínio
          </button>
        </div>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="dynamic-form">
      {/* Campo de busca de domínio */}
      <div className="form-group">
        <label className="form-label">Buscar Domínio (Opcional)</label>
        <div className="search-container">
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="form-input"
            placeholder="Digite para buscar domínios existentes..."
            onFocus={() => {
              if (searchResults.length > 0) setShowDropdown(true);
            }}
            onBlur={() => {
              setTimeout(() => {
                if (!isMouseOverDropdown) {
                  setShowDropdown(false);
                }
              }, 300);
            }}
          />
          {isSearching && (
            <div className="search-loading">
              Buscando...
            </div>
          )}
          {showDropdown && searchResults.length > 0 && searchTerm.length >= 3 && (
            <div 
              className="search-dropdown"
              onMouseEnter={() => setIsMouseOverDropdown(true)}
              onMouseLeave={() => setIsMouseOverDropdown(false)}
              onMouseDown={(e) => e.preventDefault()}
            >
              {searchResults.map((domain, index) => (
                <div
                  key={domain._id || index}
                  className="search-result-item"
                  onMouseDown={(e) => {
                    e.preventDefault();
                    handleDomainSelect(domain);
                  }}
                >
                  <div className="domain-url">{domain.url || domain.name}</div>
                  <div className="domain-info">
                    {domain.provider && <span>Provider: {domain.provider}</span>}
                    {domain.plan && <span>Plano: {domain.plan}</span>}
                    {domain.type && <span>Tipo: {domain.type}</span>}
                    {domain.name && <span>Cliente: {domain.name}</span>}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Indicador de modo */}
      {isUpdateMode && (
        <div className="form-group">
          <div className="mode-indicator">
            <span className="mode-badge update">Modo Atualização</span>
            <button type="button" onClick={handleClearForm} className="clear-button">
              Limpar e Registrar Novo
            </button>
          </div>
        </div>
      )}

      <div className="form-group">
        <label className="form-label">ID Projeto</label>
        <input name="idvenda" value={form.idvenda} onChange={handleChange} className="form-input" />
      </div>
      <div className="form-group">
        <label className="form-label">URL</label>
        <input
          name="url"
          value={form.url}
          onChange={handleChange}
          onBlur={handleUrlBlur}
          className={`form-input${urlShowError ? ' input-error' : ''}`}
          placeholder="ex: https://seudominio.com"
        />
        {urlShowError && (
          <span style={{ color: '#dc2626', fontSize: '0.9em', marginTop: 4 }}>
            Informe uma URL válida (ex: https://seudominio.com)
          </span>
        )}
      </div>
      <div className="form-group">
        <label className="form-label">IP da máquina</label>
        <input
          name="ip"
          value={form.ip}
          onChange={handleChange}
          onBlur={handleIpBlur}
          className={`form-input${ipShowError ? ' input-error' : ''}`}
          placeholder="ex: 192.168.0.1"
        />
        {ipShowError && (
          <span style={{ color: '#dc2626', fontSize: '0.9em', marginTop: 4 }}>
            Informe um IP válido (ex: 192.168.0.1)
          </span>
        )}
      </div>
      <div className="form-group">
        <label className="form-label">Nome do cliente</label>
        <input name="name" value={form.name} onChange={handleChange} className="form-input" />
      </div>
      <div className="form-group">
        <label className="form-label">Provider</label>
        {providersEnv && providersEnv.length > 0 ? (
          <select
            name="provider"
            value={form.provider || ''}
            onChange={handleChange}
            className="form-select"
          >
            <option value="">Selecione o provider</option>
            {providersEnv.map((p) => (
              <option key={p} value={p}>{p}</option>
            ))}
          </select>
        ) : (
          <input name="provider" value={form.provider || ''} onChange={handleChange} className="form-input" />
        )}
      </div>
      <div className="form-group">
        <label className="form-label">Tipo de Plano</label>
        {planosEnv && planosEnv.length > 0 ? (
          <select
            name="plan"
            value={form.plan}
            onChange={handleChange}
            className="form-select"
          >
            <option value="">Selecione tipo o Plano</option>
            {planosEnv.map((p) => (
              <option key={p} value={p}>{p}</option>
            ))}
          </select>
        ) : (
          <input name="tipo_plano" value={form.tipo_plano} onChange={handleChange} className="form-input" />
        )}
      </div>
      <div className="form-group">
        <label className="form-label">Plano</label>
        {availableTipoPlanos && availableTipoPlanos.length > 0 ? (
          <select
            name="tipo_plano"
            value={form.tipo_plano}
            onChange={handleChange}
            className="form-select"
            disabled={!form.plan} // Desabilita se nenhum plan foi selecionado
          >
            <option value="">
              {form.plan ? 'Selecione o plano' : 'Selecione primeiro o Tipo de Plano'}
            </option>
            {availableTipoPlanos.map((p) => (
              <option key={p} value={p}>{p}</option>
            ))}
          </select>
        ) : (
          <input 
            name="tipo_plano" 
            value={form.tipo_plano} 
            onChange={handleChange} 
            className="form-input"
            placeholder={form.plan ? 'Digite o plano' : 'Selecione primeiro o Tipo de Plano'}
            disabled={!form.plan}
          />
        )}
      </div>
      <div className="form-group">
        <label className="form-label">Email</label>
        <input name="email" value={form.email} onChange={handleChange} className="form-input" />
      </div>
      <div className="form-group">
        <label className="form-label checkbox-label">
          <input type="checkbox" name="update" checked={form.update} onChange={handleChange} className="form-checkbox" />
          <span className="checkbox-text">Update</span>
        </label>
      </div>
      <div className="form-actions">
        {isUpdateMode && (
          <button 
            type="button" 
            className="delete-button" 
            onClick={handleDeleteDomain}
          >
            Apagar Domínio
          </button>
        )}
        
        <button type="submit" className="submit-button" disabled={submitDisabled}>
          {isUpdateMode ? 'Atualizar Domínio' : 'Cadastrar Domínio'}
        </button>
      </div>
    </form>
  );
}